import { API, errorOf } from '../hermes';
import seed from './seed_cases.json';

export const EXECUTOR_KEY = 'agent_bench';
export const TEMPLATE_NAME = 'Agent bench';
export const DEFAULT_RUBRIC = 'The agent answers the question using the authoritative source for it, states the scope it used, proposes rather than performs any write, and keeps patient rows out of its prose.';
export const RESULT_SCHEMA = {
  type: 'object',
  properties: {
    label: { type: 'string' }, session_id: { type: 'string' }, pass: { type: 'boolean' }, passed: { type: 'integer' },
    checks_total: { type: 'integer' }, checks: { type: 'object' }, judge_score: { type: 'integer' }, judge_verdict: { type: 'string' },
    judge_reasons: { type: 'array', items: { type: 'string' } }, tool_calls: { type: 'integer' }, tools: { type: 'array', items: { type: 'string' } },
    tables: { type: 'array', items: { type: 'string' } }, hats: { type: 'array', items: { type: 'string' } }, proposals: { type: 'array' },
    session_cost_usd: { type: 'number' }, prompt_tokens: { type: 'integer' }, seconds: { type: 'number' }, reply: { type: 'string' },
    model: { type: 'string' }, thinking: { type: 'string' }, hats_enabled: { type: 'string' },
  },
};
export const SUB_TABS = [
  { key: 'cases', label: 'Cases', route: 'AdminHermesTests' },
  { key: 'runs', label: 'Runs', route: 'AdminHermesTestRuns' },
];

export function caseFrom(c, i) {
  return {
    kind: 'case', label: String(c.label || `case ${i + 1}`), description: String(c.description || ''),
    turns: Array.isArray(c.turns) ? c.turns.map(String) : [], answers: Array.isArray(c.answers) ? c.answers.map(String) : [],
    hats: String(c.hats || ''), expect: (c.expect && typeof c.expect === 'object' && !Array.isArray(c.expect)) ? c.expect : {},
    rubric: String(c.rubric || ''),
  };
}

export function seedCases() {
  return seed.map(caseFrom);
}

export async function findBench(axios) {
  const [e, t] = await Promise.all([axios.get(`${API}/executors/`), axios.get(`${API}/templates/`)]);
  const executor = e.data.find((x) => x.key === EXECUTOR_KEY) || null;
  const template = t.data.find((x) => x.name === TEMPLATE_NAME) || null;
  return { executor, template };
}

export async function setUpBench(axios, judgeModel) {
  let { executor, template } = await findBench(axios);
  if (!executor) {
    await axios.post(`${API}/executors/`, {
      key: EXECUTOR_KEY, name: 'Agent bench', runtime: 'bench', identity: 'requester', is_active: true, platform: false,
      description: 'Plays each test case through a real interactive session as the person who started the run, scores it, and grades the reply.',
      model: judgeModel, reasoning: 'medium', config: { cost_center: 'internal_ops' },
    });
    await axios.put(`${API}/executors/${EXECUTOR_KEY}/access/`, { is_enabled: true, service_user: null, cost_center: '' });
  }
  if (!template) {
    await axios.post(`${API}/templates/`, {
      name: TEMPLATE_NAME, is_active: true, unit: '', population: null, inputs: seedCases(),
      description: 'Scripted conversations the interactive agent is tested against. Edit them on the Tests tab.',
      prompt: DEFAULT_RUBRIC, result_schema: RESULT_SCHEMA, effects: [],
    });
  }
  ({ executor, template } = await findBench(axios));
  return { executor, template };
}

export function casesOf(template) {
  return (template?.inputs || []).filter((x) => x.kind === 'case').map(caseFrom);
}

export async function saveCases(axios, template, cases) {
  const others = (template.inputs || []).filter((x) => x.kind !== 'case');
  const { data } = await axios.patch(`${API}/templates/${template.id}/`, { inputs: others.concat(cases.map(caseFrom)) });
  return data;
}

export function paramsLine(run) {
  const p = run.params || {};
  const bits = [];
  if (p.model) bits.push(p.model);
  if (p.reasoning) bits.push(p.reasoning);
  if (p.hats) bits.push(`hats ${p.hats}`);
  if (p.only) bits.push(`only ${p.only}`);
  return bits.join(' · ') || 'defaults';
}

export function passLine(run) {
  const pr = run.progress || {};
  if (!pr.units) return '';
  return `${pr.passed ?? 0} / ${pr.done ?? 0} passed of ${pr.units}`;
}

export { errorOf };
